import { useCallback, useMemo } from 'react';
import {
  addProductToCart,
  getCartItemCollection,
  removeCartItem
} from '../../../application/shop/cartService';
import {
  normalizeCartErrorMessage,
  resolveStoredUserId
} from '../../../domain/shop/cartCollections';

/**
 * The cart's LINES — the rows every cart surface renders, and the state the
 * header badge counts.
 *
 * WHY EVERY MUTATION RESYNCS THE LINES. `cartItem` is the shared state behind
 * the header badge, the header dropdown, the right-rail card and the cart page.
 * Before this, only `addItemToCart` refreshed it and `deleteItemFromCart`
 * refreshed nothing at all — it dispatched an action that touches only the
 * loading and error flags. So a removal left every one of those surfaces
 * showing a row the server had already deleted, and the cart page papered over
 * it with a local set of hidden ids that no other surface could see. Each
 * mutation now ends with the server's own rows in state, so the four surfaces
 * cannot disagree about what is in the cart.
 *
 * WHAT TRIGGERS A REFRESH, EXACTLY: an add the server accepted, and a delete
 * the server accepted. Nothing polls, nothing refetches on render, and
 * `CartProvider`'s once-per-mount auto-fetch and its `feature.marketplace` skip
 * are untouched — the endpoints still 403 when the flag is off and are still
 * never called in that case.
 *
 * OPTIMISM ON DELETE, AND ONLY WHERE IT CANNOT LIE. `DELETE /cart/id/{id}/`
 * answers with an empty body, so there is no server truth to adopt at that
 * moment — which is the one case a synthesised update is warranted. The row
 * leaves state immediately and the SNAPSHOT of the previous rows is restored
 * verbatim if the request fails, so a rejected delete cannot leave a supporter
 * believing an item is gone. The add path needs no such guess: it already
 * awaits the server before refreshing.
 */
export const useCartItemsPresentation = ({
  dispatch,
  actions,
  addToast,
  setLoading,
  stateRef,
  getCart
}: {
  dispatch: any;
  actions: any;
  addToast: (payload: { message: string }) => void;
  setLoading: (status: boolean) => void;
  /** Live mirror of the reducer state, so these callbacks stay stable. */
  stateRef: { current: any };
  /** The summary slice's fetch, used only to learn a cart id we do not have. */
  getCart: (userId: any) => Promise<any>;
}) => {
  const getItemsCart = useCallback(
    async (cartId) => {
      setLoading(true);
      try {
        const items = await getCartItemCollection(cartId);
        dispatch({
          type: actions.GET_ITEMS_CART,
          payload: items
        });
        return items;
      } catch (error) {
        const message = normalizeCartErrorMessage(
          error,
          'Unknown Error - check your network connection'
        );
        addToast({ message });
        dispatch({
          type: actions.SET_ERROR,
          payload: {
            error: true,
            message
          }
        });
        return [];
      }
    },
    [actions.GET_ITEMS_CART, actions.SET_ERROR, addToast, dispatch, setLoading]
  );

  /** The cart id currently in state, unwrapped from either shape it arrives in. */
  const resolveCartId = useCallback(
    (preferred?: any) => {
      if (preferred && preferred !== 'undefined') return preferred;
      const cart = stateRef.current?.cart;
      const summary = Array.isArray(cart) ? cart[0] : cart;
      return summary?.id ?? null;
    },
    [stateRef]
  );

  /**
   * Pull the server's rows back into state after a mutation.
   *
   * The `getCart` branch covers the case the old code silently dropped: the
   * first add on an account with no cart yet passes `cartId: null`, because the
   * add is what creates the cart. The old `if (cartId)` guard meant that add
   * refreshed nothing, so the badge stayed empty until a reload. Learning the
   * new id costs one request, and only on that first add.
   */
  const resyncLines = useCallback(
    async (cartId?: any) => {
      let id = resolveCartId(cartId);
      if (!id) {
        const userId = resolveStoredUserId();
        if (!userId) return;
        const cart = await getCart(userId).catch(() => null);
        id = cart?.id ?? null;
      }
      if (!id) return;
      await getItemsCart(id);
    },
    [getCart, getItemsCart, resolveCartId]
  );

  const addItemToCart = useCallback(
    async (cartId, productId) => {
      try {
        const payload = await addProductToCart(cartId, productId);
        dispatch({
          type: actions.ADD_ITEM_TO_CART,
          payload
        });
        // Server truth rather than a guessed count: the add has already been
        // accepted, so the rows it produced are what every surface renders.
        await resyncLines(cartId);
        return payload;
      } catch (error) {
        const message = normalizeCartErrorMessage(
          error,
          'Unable to add item to cart'
        );
        addToast({ message });
        dispatch({
          type: actions.SET_ERROR,
          payload: {
            error: true,
            message
          }
        });
        return null;
      }
    },
    [
      actions.ADD_ITEM_TO_CART,
      actions.SET_ERROR,
      addToast,
      dispatch,
      resyncLines
    ]
  );

  const deleteItemFromCart = useCallback(
    async (id) => {
      // Snapshot BEFORE the optimistic removal — this is what gets restored if
      // the server refuses, and it has to be the rows as they actually were.
      const snapshot = Array.isArray(stateRef.current?.cartItem)
        ? stateRef.current.cartItem
        : [];
      dispatch({ type: actions.REMOVE_CART_LINE, payload: id });
      try {
        await removeCartItem(id);
        dispatch({
          type: actions.DELETE_ITEM_FROM_CART
        });
        // No route change here — the caller stays on the page. (Previously this
        // pushed '/shop/' to force a refresh, which yanked the user off the
        // cart on every delete.)
        await resyncLines();
        return true;
      } catch (error) {
        const message = normalizeCartErrorMessage(
          error,
          'Unable to delete cart item'
        );
        // Put the row back. A failed delete that leaves the item hidden tells a
        // supporter it is gone when it is not — the same dishonesty as a count
        // that reports a rejected add.
        dispatch({
          type: actions.GET_ITEMS_CART,
          payload: snapshot
        });
        addToast({ message });
        dispatch({
          type: actions.SET_ERROR,
          payload: {
            error: true,
            message
          }
        });
        return false;
      }
    },
    [
      actions.DELETE_ITEM_FROM_CART,
      actions.GET_ITEMS_CART,
      actions.REMOVE_CART_LINE,
      actions.SET_ERROR,
      addToast,
      dispatch,
      resyncLines,
      stateRef
    ]
  );

  return useMemo(
    () => ({ getItemsCart, addItemToCart, deleteItemFromCart }),
    [getItemsCart, addItemToCart, deleteItemFromCart]
  );
};
